/**
 * HazardDialog — the confirm for a change somebody has already read.
 *
 * ITS BODY IS A CLAUSE DIFF, NOT A WARNING. "Are you sure?" asks an admin to remember what the
 * document says; showing the sentence twice — once struck through as it stands, once in brand ink
 * as it will read — asks them only to look. That is the one import from Direction B the ruled
 * direction takes wholesale (`DESIGN-DIRECTION.md` §A5.3, §B5.3): consequence line above in the
 * destructive token, the fragment before and after, then the tabular field diff.
 *
 * THE COLLATERAL LIST IS THE THIRD PART, and it exists because the loudest consequence is rarely
 * the most expensive one. Voiding a signed agreement is visible; three preparation gates staying
 * green while describing a car that is no longer on the contract is not, and that is what actually
 * reaches activation.
 *
 * AlertDialog RATHER THAN Dialog: this is a decision, not a surface, and it should not be
 * dismissible by clicking past it. `sd-tokens` rides the content because Radix portals it to
 * `document.body`, where the dossier's custom properties no longer inherit.
 */

import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';

export interface HazardFragment {
    /** The clause as it stands, struck through. */
    old: ReactNode;
    /** The clause as it will read, in brand ink. */
    now: ReactNode;
}

export interface HazardDiffRow {
    label: string;
    old: ReactNode;
    now: ReactNode;
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    /** The consequence in plain words — what this costs somebody, not what it updates. */
    consequence: ReactNode;
    fragment?: HazardFragment;
    diffs: HazardDiffRow[];
    /** What stays true, and wrongly so, after the change. Hidden when empty. */
    collateral?: string[];
    confirmLabel: string;
    cancelLabel: string;
    pending?: boolean;
    /** The server's own refusal from a previous attempt, verbatim. */
    error?: string | null;
    onConfirm: () => void;
}

export default function HazardDialog({
    open,
    onOpenChange,
    title,
    consequence,
    fragment,
    diffs,
    collateral = [],
    confirmLabel,
    cancelLabel,
    pending,
    error,
    onConfirm,
}: Props) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="sd-tokens">
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription className="sd-hz-conseq">
                        {consequence}
                    </AlertDialogDescription>
                </AlertDialogHeader>

                {(fragment || diffs.length > 0) && (
                    <div className="sd-hz-sec">
                        <div className="sd-hz-lab">The clause, before and after</div>
                        <div className="sd-diff">
                            {fragment && (
                                <>
                                    <div className="sd-diff-row">
                                        <span className="sd-diff-k">The clause today</span>
                                        <span className="sd-diff-v sd-diff-old">{fragment.old}</span>
                                    </div>
                                    <div className="sd-diff-row">
                                        <span className="sd-diff-k">After the change</span>
                                        <span className="sd-diff-v sd-diff-new">{fragment.now}</span>
                                    </div>
                                </>
                            )}
                            {diffs.map((row) => (
                                <div className="sd-diff-row" key={row.label}>
                                    <span className="sd-diff-k">{row.label}</span>
                                    <span className="sd-diff-v">
                                        <span className="sd-diff-old">{row.old}</span>
                                        <span className="sd-diff-arrow">→</span>
                                        <span className="sd-diff-new">{row.now}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {collateral.length > 0 && (
                    <div className="sd-hz-sec">
                        <div className="sd-hz-lab">Also needs re-confirming</div>
                        <ul className="sd-coll">
                            {collateral.map((line) => (
                                <li key={line}>{line}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {error && <p className="sd-f-err">{error}</p>}

                {/*
                 * CANCEL LEFT, THE DESTRUCTIVE ACT RIGHT, AND IN THE DESTRUCTIVE TOKEN (§A5.3).
                 * `AlertDialogAction` defaults to `buttonVariants()` — the near-black primary — so
                 * without this the button that voids a signed agreement is drawn exactly like the
                 * button that saves a phone number. `AlertDialogFooter` is `sm:justify-end` with
                 * cancel first, which is what puts this one on the right.
                 */}
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
                    {/*
                     * `onSelect` PREVENTED, so the dialog stays open while the write is in flight
                     * and a refusal lands on the surface that asked for it rather than on a page
                     * the admin has already been returned to.
                     */}
                    <AlertDialogAction
                        className={buttonVariants({ variant: 'destructive' })}
                        onClick={(event) => {
                            event.preventDefault();
                            onConfirm();
                        }}
                        disabled={pending}
                    >
                        {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
